import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PayIntentResponse } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { describeBalance, formatCents } from '../../lib/money';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { TenantPaymentActivityList } from '../../components/payments/PaymentActivity';

/**
 * Tenant "Pay rent" — one-time ACH via Stripe. Two steps: (1) choose an amount → the server opens a
 * PaymentIntent (POST /api/portal/pay/intent), (2) Stripe's Payment Element collects the bank + ACH
 * mandate and confirms it. ACH settles asynchronously, so a confirm returns `processing`; the
 * `payment_intent.succeeded` webhook posts it to the ledger once it clears — this screen never
 * writes the ledger itself.
 *
 * 0.26.0 — every step is recorded: opening this page records a payment attempt (an open one for the
 * same amount is reused), the confirm result is reported back (advisory; Stripe's webhook is the
 * truth), `?attempt=<id>` continues an unfinished payment, and the tenant's recent attempts are
 * listed in plain language (processing = pending, never "paid" until it clears).
 */

// Load Stripe.js ONCE per publishable key (module scope — loadStripe must not be called on render).
const stripeLoaders = new Map<string, Promise<StripeJs | null>>();
function stripePromiseFor(pk: string): Promise<StripeJs | null> {
  const cached = stripeLoaders.get(pk);
  if (cached) return cached;
  const p = loadStripe(pk);
  stripeLoaders.set(pk, p);
  return p;
}

/** Dollars string → positive integer cents, or null (blank / non-positive / NaN). */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/** The confirm step — rendered inside <Elements> once a PaymentIntent exists. */
function ConfirmForm({ intent, onReset }: { intent: PayIntentResponse; onReset: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<'processing' | 'action' | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErr(null);
    const result = await stripe.confirmPayment({
      elements,
      // return_url is only used if Stripe needs a redirect; `if_required` keeps ACH inline.
      confirmParams: { return_url: `${window.location.origin}/portal/ledger` },
      redirect: 'if_required',
    });
    setSubmitting(false);
    // Report what Stripe.js said (best-effort, advisory — the webhook is authoritative and the server
    // only moves the attempt FORWARD to requires_action / processing; it never marks it paid).
    const reported = result.error ? result.error.payment_intent?.status : result.paymentIntent?.status;
    if (reported) {
      try {
        await api.portal.paymentAttempts.confirmResult(intent.attemptId, { status: reported });
      } catch {
        /* the webhook will record it anyway */
      }
    }
    void queryClient.invalidateQueries({ queryKey: ['portal', 'payment-attempts'] });
    void queryClient.invalidateQueries({ queryKey: ['portal', 'overview'] });
    if (result.error) {
      setErr(result.error.message ?? 'Payment could not be completed.');
      return;
    }
    // ACH clears over a few days → status is usually 'processing'. Even a test-mode 'succeeded' is
    // shown as submitted here: the ledger (and "paid") only follow Stripe's webhook.
    setDone(result.paymentIntent?.status === 'requires_action' ? 'action' : 'processing');
  }

  if (done) {
    return (
      <StatusMessage tone="ok">
        {done === 'action'
          ? 'Almost done — your bank account needs to be verified before the payment can be sent. Watch for small test deposits and use the verification link under "Your recent online payments". '
          : 'Payment submitted — it is now processing. Bank transfers usually take up to 4 business days to clear; it posts to your ledger automatically once it settles and is shown as pending until then. '}
        <Link to="/portal/ledger">View your ledger</Link>
      </StatusMessage>
    );
  }

  return (
    <form className="stack" onSubmit={submit}>
      {err && <StatusMessage tone="err">{err}</StatusMessage>}
      <PaymentElement
        options={{
          // This rail is ACH-only: the PaymentIntent pins `payment_method_types: ['us_bank_account']`
          // (see apps/api/src/routes/portal.ts), so no payment method other than the US bank account
          // can appear as its own tab. The one exception is the Link wallet — enabled at the account
          // level, it surfaces INSIDE the Payment Element and offers Klarna/card ("… Powered by Link")
          // alongside the bank option. Turning Link off here (with Apple/Google Pay, which this rail
          // also never uses) leaves exactly the US bank account (ACH) option and nothing else.
          wallets: { link: 'never', applePay: 'never', googlePay: 'never' },
          // Belt-and-braces ordering: keep the bank option first even if the method set ever widens.
          paymentMethodOrder: ['us_bank_account'],
        }}
      />
      <div className="row">
        <button type="submit" disabled={!stripe || submitting}>
          {submitting ? 'Submitting…' : `Pay ${formatCents(intent.amountCents)} by bank (ACH)`}
        </button>
        <button type="button" className="secondary" onClick={onReset} disabled={submitting}>
          Change amount
        </button>
      </div>
      <p className="muted" style={{ fontSize: '0.85rem', margin: 0 }}>
        Paying by ACH bank transfer — there is no fee to you. The portal is not the only way to pay:
        you may always pay by check with no added fee.
      </p>
    </form>
  );
}

export function Pay() {
  const config = useQuery({
    queryKey: ['portal', 'pay-config'],
    queryFn: () => api.portal.pay.config(),
  });
  const ledger = useQuery({ queryKey: ['portal', 'ledger'], queryFn: () => api.portal.ledger() });
  const queryClient = useQueryClient();
  const attempts = useQuery({ queryKey: ['portal', 'payment-attempts'], queryFn: () => api.portal.paymentAttempts.list() });
  const cancelAttempt = useMutation({
    mutationFn: (id: string) => api.portal.paymentAttempts.cancel(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['portal', 'payment-attempts'] }),
  });
  const [params, setParams] = useSearchParams();
  const resumeId = params.get('attempt');

  const [amount, setAmount] = useState('');
  const [amountInit, setAmountInit] = useState(false);
  const [intent, setIntent] = useState<PayIntentResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Default the amount field to what the tenant owes, once, when the ledger loads.
  const balanceCents = ledger.data?.balanceCents ?? 0;
  useEffect(() => {
    if (ledger.data && !amountInit) {
      setAmountInit(true);
      if ((ledger.data.balanceCents ?? 0) > 0) {
        setAmount((ledger.data.balanceCents / 100).toFixed(2));
      }
    }
  }, [ledger.data, amountInit]);

  // `?attempt=<id>` — continue an unfinished payment (the server re-reads the PaymentIntent).
  useEffect(() => {
    if (!resumeId || intent) return;
    let cancelled = false;
    api.portal.paymentAttempts
      .resume(resumeId)
      .then((r) => {
        if (!cancelled) setIntent(r);
      })
      .catch((e2: unknown) => {
        if (!cancelled) setErr(errorMessage(e2));
      })
      .finally(() => {
        if (!cancelled) setParams({}, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [resumeId, intent, setParams]);

  const pk = config.data?.publishableKey ?? null;
  const stripePromise = useMemo(() => (pk ? stripePromiseFor(pk) : null), [pk]);

  if (config.isLoading || ledger.isLoading) return <Loading label="Loading payment options…" />;
  if (config.isError || !config.data)
    return <ErrorState message={errorMessage(config.error)} onRetry={() => config.refetch()} />;

  if (!config.data.enabled) {
    return (
      <>
        <h1>Pay rent</h1>
        <StatusMessage tone="info">
          Online payment isn&rsquo;t available yet. Please pay by one of the accepted methods on the{' '}
          <Link to="/portal/payment-methods">Payment methods</Link> page.
        </StatusMessage>
      </>
    );
  }

  async function createIntent(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const cents = dollarsToCents(amount);
    if (cents == null) {
      setErr('Enter a valid amount greater than zero.');
      return;
    }
    setCreating(true);
    try {
      setIntent(await api.portal.pay.intent({ amountCents: cents }));
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setCreating(false);
    }
  }

  const balance = describeBalance(balanceCents);

  return (
    <>
      <h1>Pay rent</h1>
      <p className="subtitle">Pay by bank transfer (ACH) — no fee to you.</p>

      <div className="stat" style={{ maxWidth: 320 }}>
        <p className="stat-label">Current balance</p>
        <p className="stat-value">{balance.amount}</p>
        <p className="muted" style={{ margin: 0 }}>
          {balance.label}
        </p>
        {(ledger.data?.pendingPaymentCents ?? 0) > 0 && (
          <p className="muted" style={{ margin: '0.25rem 0 0' }}>
            Pending: {formatCents(ledger.data?.pendingPaymentCents ?? 0)} bank payment processing — not yet applied to
            your balance.
          </p>
        )}
      </div>

      {attempts.data && attempts.data.attempts.length > 0 && (
        <section aria-labelledby="recent-pay-heading" className="stack" style={{ maxWidth: 560 }}>
          <h2 id="recent-pay-heading">Your recent online payments</h2>
          {cancelAttempt.isError && <StatusMessage tone="err">{errorMessage(cancelAttempt.error)}</StatusMessage>}
          <TenantPaymentActivityList
            attempts={attempts.data.attempts}
            onCancel={(a) => cancelAttempt.mutate(a.id)}
            busy={cancelAttempt.isPending}
            limit={3}
          />
        </section>
      )}

      {!intent ? (
        <form className="card stack" onSubmit={createIntent} style={{ maxWidth: 420 }}>
          {err && <StatusMessage tone="err">{err}</StatusMessage>}
          <div>
            <label htmlFor="pay-amount">Amount to pay (USD)</label>
            <input
              id="pay-amount"
              type="number"
              step="0.01"
              min="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div>
            <button type="submit" disabled={creating}>
              {creating ? 'Starting…' : 'Continue to bank details'}
            </button>
          </div>
        </form>
      ) : stripePromise ? (
        <div className="card" style={{ maxWidth: 480 }}>
          <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret }}>
            <ConfirmForm intent={intent} onReset={() => setIntent(null)} />
          </Elements>
        </div>
      ) : (
        <StatusMessage tone="err">Payment is temporarily unavailable.</StatusMessage>
      )}
    </>
  );
}
